import * as THREE from "three"
import { TextGeometry } from "three/examples/jsm/Addons.js"
import Experience from "../../../Experience"
import Home from "./Home"
import Welcome from "./Welcome.js"
import Description from "./Description.js"
import gsap from "gsap"


export default class Interaction {

    constructor() {

        this.experience = new Experience()
        this.home = new Home()
        this.scene = this.home.scene
        this.objects = []
        this.resources = this.experience.resources
        this.index = 1
        this.menuItems = []
        this.highlightedColor = ""
        this.defaultColor = ""
        this.welcomePage = false
        this.homePageIndex = 0
        this.highlightedSize = 0
        this.currentImage = null
        this.currentDescription = null
        this.menuImages = null
        this.isScrollable = false
        this.scrollablePage = null

        this.resources.on("ready", () => {
            this.selector = this.resources.items.selector
            this.hoverSound = this.resources.items.hoverSound
            this.selectedSound = this.resources.items.selectedSound
            this.backSound = this.resources.items.backSound

            if (this.hoverSound) this.hoverSound.volume = 0.5
            if (this.selectedSound) this.selectedSound.volume = 0.5
            if (this.backSound) this.backSound.volume = 0.5
        })

    }

    setScrollable(page) {
        this.isScrollable = true
        this.scrollablePage = page
        this.menuItems = []
        this.welcomePage = false

        if (this.selectorLeft) {
            this.scene.remove(this.selectorLeft)
            if (this.selectorLeft.geometry) this.selectorLeft.geometry.dispose()
            if (this.selectorLeft.material) this.selectorLeft.material.dispose()
            this.selectorLeft = null
        }
        if (this.selectorRight) {
            this.scene.remove(this.selectorRight)
            if (this.selectorRight.geometry) this.selectorRight.geometry.dispose()
            if (this.selectorRight.material) this.selectorRight.material.dispose()
            this.selectorRight = null
        }

        this.setKeyboard(false)
    }

    setPrefs(optionsList, highlightedColor, defaultColor, welcomePage, highlightedSize, currentImage = null, menuImages = null, currentDescription = null, primaryColor = null, secondaryColor = null, pageTitle = null) {
        this.isScrollable = false
        this.scrollablePage = null
        this.pagePrimaryColor = primaryColor
        this.pageSecondaryColor = secondaryColor
        this.pageTitle = pageTitle || this.home.currentPage?.template?.pageTitle || (welcomePage ? "welcome" : null)

        const pageKey = welcomePage ? "welcome" : (this.pageTitle || "default")
        this.lastIndices = this.lastIndices || {}
        this.index = welcomePage 
            ? this.homePageIndex 
            : (this.lastIndices[pageKey] !== undefined ? Math.min(this.lastIndices[pageKey], optionsList.length - 1) : 0)

        this.menuItems = optionsList
        this.highlightedColor = highlightedColor
        this.welcomePage = welcomePage
        this.highlightedSize = highlightedSize
        this.defaultColor = defaultColor
        this.currentImage = currentImage
        this.menuImages = menuImages
        this.currentDescription = currentDescription

        this.setKeyboard(true)
        this.updateSelection()
    }

    setKeyboard(createSelectors = true) {
        if (createSelectors) {
            if (this.selectorLeft) {
                this.scene.remove(this.selectorLeft)
                if (this.selectorLeft.geometry) this.selectorLeft.geometry.dispose()
                if (this.selectorLeft.material) this.selectorLeft.material.dispose()
                this.selectorLeft = null
            }
            if (this.selectorRight) {
                this.scene.remove(this.selectorRight)
                if (this.selectorRight.geometry) this.selectorRight.geometry.dispose()
                if (this.selectorRight.material) this.selectorRight.material.dispose()
                this.selectorRight = null
            }

            const selectorMaterial = new THREE.MeshBasicMaterial({
                map: this.selector,
                transparent: true
            })
            this.selectorLeft = new THREE.Mesh(
                new THREE.PlaneGeometry(0.8, 1.0),
                selectorMaterial
            )
            if (this.welcomePage) {
                this.selectorRight = new THREE.Mesh(
                    new THREE.PlaneGeometry(0.8, 1.0),
                    selectorMaterial.clone()
                )
                this.selectorRight.scale.set(-0.7, 0.7, 0.7)
                this.scene.add(this.selectorRight)
            }
            this.selectorLeft.scale.set(0.7, 0.7, 0.7)
            this.scene.add(this.selectorLeft)
        }

        if (this.keydownHandler) {
            window.removeEventListener('keydown', this.keydownHandler)
        }

        this.keydownHandler = (event) => {
            if (event.code === 'KeyW' || event.code === 'ArrowUp') {
                this.moveUp()
            }
            else if (event.code === 'KeyS' || event.code === 'ArrowDown') {
                this.moveDown()
            }
            else if (event.code === 'Space' || event.code === 'Enter') {
                this.confirm()
            }
            else if (event.code === 'Escape') {
                this.back()
            }
        }
        window.addEventListener('keydown', this.keydownHandler)
    }

    moveUp() {
        if (this.isScrollable && this.scrollablePage) {
            const moved = this.scrollablePage.scrollUp()
            if (moved && this.hoverSound) {
                this.hoverSound.currentTime = 0
                this.hoverSound.play().catch(() => { })
            }
            return
        }

        if (!this.menuItems.length) {
            return
        }

        this.index--
        if (this.index < 0) this.index = this.menuItems.length - 1
        const pageKey = this.welcomePage ? "welcome" : (this.pageTitle || this.home.currentPage?.template?.pageTitle || "default")
        this.lastIndices = this.lastIndices || {}
        this.lastIndices[pageKey] = this.index
        this.updateSelection()
        if (this.hoverSound) {
            this.hoverSound.currentTime = 0
            this.hoverSound.play().catch(() => { })
        }
    }

    moveDown() {
        if (this.isScrollable && this.scrollablePage) {
            const moved = this.scrollablePage.scrollDown()
            if (moved && this.hoverSound) {
                this.hoverSound.currentTime = 0
                this.hoverSound.play().catch(() => { })
            }
            return
        }

        if (!this.menuItems.length) {
            return
        }

        this.index++
        if (this.index > this.menuItems.length - 1) this.index = 0
        const pageKey = this.welcomePage ? "welcome" : (this.pageTitle || this.home.currentPage?.template?.pageTitle || "default")
        this.lastIndices = this.lastIndices || {}
        this.lastIndices[pageKey] = this.index
        this.updateSelection()
        if (this.hoverSound) {
            this.hoverSound.currentTime = 0
            this.hoverSound.play().catch(() => { })
        }
    }

    confirm() {
        if (this.isScrollable) {
            if (this.scrollablePage && typeof this.scrollablePage.confirm === "function") {
                this.scrollablePage.confirm()
            }
            return
        }

        if (!this.menuItems.length || this.isConfirming) {
            return
        }

        this.isConfirming = true
        const selectedItem = this.menuItems[this.index]
        selectedItem.material.transparent = true
        selectedItem.material.needsUpdate = true

        if (this.selectedSound) {
            this.selectedSound.currentTime = 0
            this.selectedSound.play().catch(() => { })
        }

        gsap.to(selectedItem.material, {
            opacity: 0,
            duration: 0.1,
            yoyo: true,
            repeat: 3,
            onComplete: () => {
                selectedItem.material.opacity = 1
                this.isConfirming = false
                this.select(this.index)
            }
        })
    }

    select(index) {
        if (this.welcomePage) {
            const PageClass = this.menuItems[index].page
            if (PageClass) {
                this.homePageIndex = index
                this.home.setPage(PageClass)
            }
        } else {
            const pageTitle = this.pageTitle || this.home.currentPage?.template?.pageTitle
            const isDetailPage = pageTitle !== "Contact"

            if (isDetailPage) {
                const item = this.menuImages && this.menuImages[index]
                if (item) {
                    // Support object format { name, image, listDescription, link, detailedDescription }
                    // as well as the legacy array format [title, image, desc, link?, longDesc?]
                    const isObject = !Array.isArray(item) && typeof item === "object"

                    const title = isObject ? item.name : item[0]
                    const image = isObject ? item.image : item[1]
                    const shortDescription = isObject ? item.listDescription : item[2]

                    let link = isObject ? (item.link || null) : null
                    let detailedDescription = isObject ? (item.detailedDescription || shortDescription) : shortDescription

                    if (!isObject) {
                        if (item.length > 4) {
                            link = item[3] || null
                            detailedDescription = item[4] || shortDescription
                        } else if (item.length === 4) {
                            const fourthItem = item[3]
                            if (typeof fourthItem === "string" && (
                                fourthItem.startsWith("http://") ||
                                fourthItem.startsWith("https://") ||
                                fourthItem.startsWith("mailto:") ||
                                fourthItem.startsWith("/") ||
                                fourthItem.endsWith(".pdf")
                            )) {
                                link = fourthItem
                                detailedDescription = shortDescription
                            } else {
                                detailedDescription = fourthItem || shortDescription
                            }
                        }
                    }

                    this.home.setPage(Description, title, detailedDescription, image, link)
                    return
                }
            }

            const fallbackItem = this.menuImages && this.menuImages[index]
            if (fallbackItem) {
                const isObject = !Array.isArray(fallbackItem) && typeof fallbackItem === "object"
                const link = isObject ? (fallbackItem.link || null) : fallbackItem[3]
                if (link) {
                    if (link.endsWith('.pdf')) {
                        const a = document.createElement('a');
                        a.href = link;
                        a.download = link.substring(link.lastIndexOf('/') + 1) || 'Resume.pdf';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                    } else if (link.startsWith('mailto:')) {
                        window.location.href = link;
                    } else {
                        window.open(link, '_blank');
                    }
                }
            }
        }
    }

    back() {
        if (this.welcomePage) {
            return
        }
        if (this.backSound) {
            this.backSound.currentTime = 0
            this.backSound.play().catch(() => { })
        }
        if (this.isScrollable) {
            const returnPage = this.home.previousPage || Welcome
            this.home.setPage(returnPage, ...(this.home.previousPageArgs || []))
            return
        }
        this.home.setPage(Welcome)
    }

    // returningCurrentIndex() {
    //     return this.index
    // }

    updateSelection() {
        if (this.isScrollable || !this.menuItems.length || !this.selectorLeft) {
            return
        }


        for (let i = 0; i < this.menuItems.length; i++) {
            const item = this.menuItems[i]
            if (i === this.index) {
                item.material.color.set(this.highlightedColor)
                item.scale.set(1.2, 1.2, 1.2)

                // Update selectors position
                item.geometry.computeBoundingBox()
                const minX = item.geometry.boundingBox.min.x * item.scale.x
                const maxX = item.geometry.boundingBox.max.x * item.scale.x
                const minY = item.geometry.boundingBox.min.y * item.scale.y
                const maxY = item.geometry.boundingBox.max.y * item.scale.y
                const centerY = (minY + maxY) / 2

                this.selectorLeft.position.set(
                    item.position.x + minX - 0.8,
                    item.position.y + centerY,
                    item.position.z
                )

                if (this.welcomePage) {

                    this.selectorRight.position.set(
                        item.position.x + maxX + 0.8,
                        item.position.y + centerY,
                        item.position.z
                    )
                }
            }
            else {
                item.material.color.set(this.defaultColor)
                item.scale.set(1, 1, 1)
            }
        }
        if (!this.welcomePage && this.currentImage)
            this.updatingImage()

    }

    updatingImage() {
        const item = this.menuImages[this.index]
        const isObject = !Array.isArray(item) && typeof item === "object"
        const texture = isObject ? item.image : item[1]
        texture.colorSpace = THREE.SRGBColorSpace
        texture.needsUpdate = true
        this.currentImage.material.map = texture
        this.currentImage.material.opacity = 1
        this.currentImage.material.needsUpdate = true

        if (this.currentDescription) {
            const maxCharactersPerLine = this.currentDescription.userData.maxCharactersPerLine ?? 24
            const rawDesc = isObject ? item.listDescription : item[2]
            const description = this.wrapText(rawDesc, maxCharactersPerLine)
            const geometry = new TextGeometry(description, {
                font: this.currentDescription.geometry.parameters.options.font,
                size: this.currentDescription.geometry.parameters.options.size,
                depth: 0,
                curveSegments: 12
            })

            this.currentDescription.geometry.dispose()
            this.currentDescription.geometry = geometry
            this.currentDescription.text = description
        }
    }

    wrapText(value, maxCharactersPerLine = 24) {
        if (!value) {
            return ""
        }

        const words = value.split(" ")
        const lines = []
        let currentLine = ""

        for (const word of words) {
            const nextLine = currentLine ? `${currentLine} ${word}` : word

            if (nextLine.length > maxCharactersPerLine && currentLine) {
                lines.push(currentLine)
                currentLine = word
            }
            else {
                currentLine = nextLine
            }
        }

        if (currentLine) {
            lines.push(currentLine)
        }

        return lines.join("\n")
    }

    destroy() {
        if (this.keydownHandler) {
            window.removeEventListener('keydown', this.keydownHandler)
        }

        if (this.selectorLeft) {
            this.scene.remove(this.selectorLeft)
            if (this.selectorLeft.geometry) this.selectorLeft.geometry.dispose()
            if (this.selectorLeft.material) this.selectorLeft.material.dispose()
            this.selectorLeft = null
        }

        if (this.selectorRight) {
            this.scene.remove(this.selectorRight)
            if (this.selectorRight.geometry) this.selectorRight.geometry.dispose()
            if (this.selectorRight.material) this.selectorRight.material.dispose()
            this.selectorRight = null
        }

        this.isScrollable = false
        this.scrollablePage = null
    }
}
